import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const CHAT_WIDGET_SCRIPT_ID = "md-chat-widget-script";
export const CHAT_WIDGET_URL = "http://44.244.104.43/embed.js";
export const CHAT_WIDGET_API_URL = "http://44.244.104.43";
export const CHAT_WIDGET_NODE_CLASS = "math-dingo-chat-widget-node";

const CHAT_WIDGET_SELECTORS = [
  '[id*="md-chat-widget"]',
  '[class*="md-chat-widget"]',
  "[data-md-chat-widget]",
  `iframe[src*="${CHAT_WIDGET_API_URL}"]`,
  'iframe[src*="md-mu-lemon.vercel.app"]',
];

const getChatWidgetNodes = () =>
  CHAT_WIDGET_SELECTORS.flatMap((selector) =>
    Array.from(document.querySelectorAll(selector)),
  ).filter((node) => node.id !== CHAT_WIDGET_SCRIPT_ID);

const getChatWidgetRootNode = (node) => {
  let rootNode = node;

  while (rootNode.parentElement && rootNode.parentElement !== document.body) {
    rootNode = rootNode.parentElement;
  }

  return rootNode;
};

const getChatWidgetRootNodes = () =>
  Array.from(new Set(getChatWidgetNodes().map(getChatWidgetRootNode)));

const tagChatWidgetNodes = () => {
  getChatWidgetRootNodes().forEach((node) => {
    node.classList.add(CHAT_WIDGET_NODE_CLASS);
  });
};

const getVisibleViewport = () => {
  const visualViewport = window.visualViewport;

  return {
    height: visualViewport?.height || window.innerHeight,
    offsetTop: visualViewport?.offsetTop || 0,
    width: visualViewport?.width || window.innerWidth,
  };
};

const constrainChatWidgetToVisibleViewport = () => {
  tagChatWidgetNodes();

  const { height, offsetTop, width } = getVisibleViewport();
  const isCompactViewport =
    width <= 950 ||
    window.matchMedia("(max-width: 1024px) and (orientation: portrait)").matches;

  getChatWidgetRootNodes().forEach((node) => {
    if (!isCompactViewport) {
      node.style.removeProperty("--md-chat-visible-height");
      node.style.removeProperty("--md-chat-visible-bottom");
      node.style.removeProperty("max-height");
      node.style.removeProperty("top");
      return;
    }

    const safeGap = 8;
    const visibleHeight = Math.max(180, height - safeGap * 2);
    const visibleBottom = Math.max(safeGap, window.innerHeight - offsetTop - height + safeGap);

    node.style.setProperty("--md-chat-visible-height", `${visibleHeight}px`);
    node.style.setProperty("--md-chat-visible-bottom", `${visibleBottom}px`);
    node.style.maxHeight = `${visibleHeight}px`;
    node.style.removeProperty("top");
  });
};

const setChatWidgetVisibility = (isVisible) => {
  tagChatWidgetNodes();
  getChatWidgetNodes().forEach((node) => {
    node.style.display = isVisible ? "" : "none";
    node.setAttribute("aria-hidden", isVisible ? "false" : "true");
  });

  if (isVisible) {
    constrainChatWidgetToVisibleViewport();
  }
};

const removeChatWidgetScript = () => {
  document.getElementById(CHAT_WIDGET_SCRIPT_ID)?.remove();
  delete window.SproutWidgetConfig;
};

const removeChatWidgetFrame = () => {
  getChatWidgetRootNodes().forEach((node) => node.remove());
};

/**
 * Returns true when the device is in mobile portrait orientation — the same
 * condition under which the app's responsive-orientation-shell rotates the UI
 * into landscape.  embed.js's "mobile-landscape" variant handles the widget
 * rotation/sizing natively in this scenario.
 */
const isMobilePortrait = () =>
  window.innerWidth <= 1024 &&
  window.matchMedia("(orientation: portrait)").matches;

const isDesktopViewport = () =>
  window.matchMedia("(min-width: 1025px)").matches;

const isLikelyExpandedChatWidget = (node) => {
  const rect = node.getBoundingClientRect();
  return rect.width > 180 || rect.height > 180;
};

const closeChatWidgetModal = () => {
  const expandedWidgetNodes = getChatWidgetRootNodes().filter(
    isLikelyExpandedChatWidget,
  );

  if (!expandedWidgetNodes.length) return;

  expandedWidgetNodes.forEach((node) => {
    const iframe = node.querySelector("iframe");

    try {
      const launcher = iframe?.contentDocument?.getElementById("chat-launcher");
      const isOpen =
        launcher?.getAttribute("aria-expanded") === "true" ||
        isLikelyExpandedChatWidget(node);

      if (launcher && isOpen) {
        launcher.click();
      }
    } catch {
      // Cross-origin iframe: do nothing rather than forcing a parent resize that
      // leaves the iframe's launcher in the wrong visual state.
    }
  });
};

const appendChatWidgetScript = () => {
  const useMobileLandscape = isMobilePortrait();

  // Tell embed.js to use its native mobile-landscape layout via global config
  if (useMobileLandscape) {
    window.SproutWidgetConfig = {
      ...(window.SproutWidgetConfig || {}),
      variant: "mobile-landscape",
    };
  }

  const script = document.createElement("script");
  script.id = CHAT_WIDGET_SCRIPT_ID;
  script.src = CHAT_WIDGET_URL;
  script.async = true;
  script.setAttribute("data-api-url", CHAT_WIDGET_API_URL);
  script.setAttribute("data-position", "right");

  // Also pass variant as a data-attribute for redundancy
  if (useMobileLandscape) {
    script.setAttribute("data-variant", "mobile-landscape");
  }

  document.body.appendChild(script);
  return script;
};

const ensureChatWidgetScript = () => {
  const existingScript = document.getElementById(CHAT_WIDGET_SCRIPT_ID);
  if (existingScript) return existingScript;
  return appendChatWidgetScript();
};

const reloadChatWidgetScript = () => {
  removeChatWidgetScript();
  return appendChatWidgetScript();
};

const MathDingoChatWidget = ({ canUseChatbot = true }) => {
  const location = useLocation();
  const shouldShowChatWidget =
    canUseChatbot && location.pathname === "/yearsdashboard";

  useEffect(() => {
    document.body.classList.toggle(
      "math-dingo-chat-widget-active",
      shouldShowChatWidget,
    );

    return () => {
      document.body.classList.remove("math-dingo-chat-widget-active");
    };
  }, [shouldShowChatWidget]);

  useEffect(() => {
    setChatWidgetVisibility(shouldShowChatWidget);

    if (typeof MutationObserver === "undefined") return undefined;

    const observer = new MutationObserver(() => {
      setChatWidgetVisibility(shouldShowChatWidget);
      constrainChatWidgetToVisibleViewport();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [shouldShowChatWidget]);

  useEffect(() => {
    if (!shouldShowChatWidget) {
      setChatWidgetVisibility(false);
      if (!canUseChatbot) {
        removeChatWidgetFrame();
        removeChatWidgetScript();
      }
      return undefined;
    }

    setChatWidgetVisibility(true);
    ensureChatWidgetScript();

    const retryDelays = [1200, 3000];
    const retryIds = retryDelays.map((delay) =>
      window.setTimeout(() => {
        const hasWidgetNode = getChatWidgetNodes().length > 0;
        if (!hasWidgetNode) {
          reloadChatWidgetScript();
        }
        setChatWidgetVisibility(true);
      }, delay),
    );

    return () => {
      retryIds.forEach((retryId) => window.clearTimeout(retryId));
    };
  }, [canUseChatbot, shouldShowChatWidget]);

  useEffect(() => {
    if (!shouldShowChatWidget) return undefined;

    const handleViewportChange = () => {
      constrainChatWidgetToVisibleViewport();
    };

    handleViewportChange();

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("orientationchange", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("orientationchange", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
    };
  }, [shouldShowChatWidget]);

  useEffect(() => {
    if (!shouldShowChatWidget) return undefined;

    const handleDesktopOutsidePointerDown = (event) => {
      if (!isDesktopViewport()) return;

      const widgetRootNodes = getChatWidgetRootNodes();
      const clickedInsideWidget = widgetRootNodes.some((node) =>
        node.contains(event.target),
      );

      if (clickedInsideWidget) return;

      closeChatWidgetModal();
    };

    document.addEventListener("pointerdown", handleDesktopOutsidePointerDown);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleDesktopOutsidePointerDown,
      );
    };
  }, [shouldShowChatWidget]);

  return null;
};

export default MathDingoChatWidget;
